import React, { useState, useEffect } from "react";
import {
  Dialog,
  ErrorMessage,
  PrimaryButton,
  RadioButton,
  SecondaryButton,
  SingleSelect,
  Text,
  Textfield,
} from "widgets";
import { Stepper, Step, StepLabel } from "@mui/material";
import {
  useFetchPartPaymentDetails,
  useCreatePaymentLink,
  useCreatePartPaymentSchedule,
} from "store/backend/assets";
import {
  FIRST_INSTALLMENT_NUMBER,
  FULL_PAYMENT,
  JODO_FLEX,
  KH_VERTICAL_ID,
  LEARN_PROVIDERS,
  LEARN_VERTICAL_ID,
  PART_PAYMENT_FULLY_PAID_STATUS_ID,
  PART_PAYMENT_PARTIALLY_PAID_STATUS_ID,
  PAYMENT_PLANS,
  SCA_PROVIDERS,
  ONE_RUPEE,
} from "constants/config";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-fox-toast";
import EditIcon from "@mui/icons-material/Edit";
import ConfirmSchedule from "components/ConfirmSchedule/ConfirmSchedule";
import Loader from "Shared/Loader/Loader";
import PaymentModes from "components/PaymentModes/PaymentModes";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { formatMonetaryAmount } from "utils/formatMonetaryAmount";

import styles from "./PartPayment.module.scss";

const CUSTOM_AMOUNT_PAYMENT_TYPE_ID = 2;

const handleOnClickCreatePaymentLink = (
  selectedOrderId,
  stepIndex,
  amount,
  amountPaid,
  createPaymentLink,
  setPaymentLinks,
  setIsLinkCopied,
  isPartAmountPartiallyPaid,
  currencyId,
) => {
  const paymentAmount = isPartAmountPartiallyPaid
    ? amount - amountPaid
    : amount;

  const payload = {
    order_id: selectedOrderId,
    payment_link_amount: paymentAmount,
    currency_id: currencyId,
    payment_type_id: 2,
    payment_flow_type_id: 2,
  };

  createPaymentLink(payload, {
    onSuccess: (paymentLink) => {
      setPaymentLinks((prev) => ({ ...prev, [stepIndex]: paymentLink }));
      setIsLinkCopied((prev) => ({ ...prev, [stepIndex]: false }));
    },
  });
};

const handleCopyLink = (stepIndex, paymentLinks, setIsLinkCopied) => {
  navigator.clipboard.writeText(paymentLinks[stepIndex]);
  setIsLinkCopied((prev) => ({ ...prev, [stepIndex]: true }));
  toast.success("Copied Payment Link!");
};

const handlePartChange = (
  event,
  setSelectedOption,
  setActiveStep,
  setPaymentLinks,
  setIsLinkCopied
) => {
  setSelectedOption(event.target.value);
  setActiveStep(0);
  setPaymentLinks({});
  setIsLinkCopied({});
};

const handleCloseConfirmDialog = (setOpenDialogContent) => {
  setOpenDialogContent(false);
};

const handleConfirmAndProceed = (
  partPaymentDetails,
  selectedOption,
  selectedOrderId,
  createPartPaymentSchedule,
  provider,
  openSnackbar
) => {
  const selectedPartDetails = partPaymentDetails.installment_plans.find(
    (plan) => plan.total_schedules.toString() === selectedOption
  );

  if (!selectedPartDetails) {
    toast.error("Selected payment option is not available.");
    return;
  }

  const payload = {
    order_id: selectedOrderId,
    provider_id: provider,
    installment_plans: [
      {
        ...(selectedPartDetails.sku_subscription_id && {
          sku_subscription_id: selectedPartDetails.sku_subscription_id,
        }),
        ...(selectedPartDetails.id && {
          id: selectedPartDetails.id,
        }),
        total_schedules: selectedPartDetails.total_schedules,
        schedules: selectedPartDetails.schedules.map((schedule, index) => ({
          sequence_number: index + 1,
          due_date: schedule.due_date,
          includes_books: schedule.includes_books,
          amount: schedule.amount,
        })),
      },
    ],
  };

  createPartPaymentSchedule(payload, {
    onSuccess: () => {
      openSnackbar("Part payment schedule confirmed successfully", "success");
      window.location.reload();
    },
    onError: (error) =>
      openSnackbar(
        error?.message || "Failed to create part payment schedule",
        "error"
      ),
  });
};

const getInstallments = (
  isChangeSchedule,
  isPartPaymentScheduleConfirmed,
  currentPartSchedule,
  selectedPartPaymentDetails
) => {
  if (isChangeSchedule) return currentPartSchedule;
  if (isPartPaymentScheduleConfirmed) {
    return [...(selectedPartPaymentDetails[0]?.installments || [])].sort(
      (installmentA, installmentB) =>
        installmentA.installment_no - installmentB.installment_no
    );
  }
  return currentPartSchedule;
};

const handleCopyMandateLink = (url) => {
  if (!url) return;

  navigator.clipboard
    .writeText(url)
    .then(() => {
      toast.success("Jodo mandate link copied!");
    })
    .catch(() => {
      toast.error("Failed to copy mandate link.");
    });
};
const getDefaultProvider = (selectedPartPaymentDetails) => {
  if (
    Array.isArray(selectedPartPaymentDetails) &&
    selectedPartPaymentDetails.length > 0 &&
    selectedPartPaymentDetails[0]?.provider?.id
  ) {
    return selectedPartPaymentDetails[0].provider.id;
  }
  return null;
};
const showCopyMandateLinkButton = (
  selectedPartPaymentDetails,
  handleCopyMandateLink
) => {
  if (
    !Array.isArray(selectedPartPaymentDetails) ||
    selectedPartPaymentDetails.length === 0
  ) {
    return null;
  }

  const mandateUrl = selectedPartPaymentDetails[0]?.mandate?.redirection_url;

  if (!mandateUrl) return null;

  return (
    <div style={{ marginTop: "10px" }}>
      <SecondaryButton onClick={() => handleCopyMandateLink(mandateUrl)}>
        Copy link to setup e-mandate
      </SecondaryButton>
    </div>
  );
};


const getProvidersList = (selectedOrderVerticalId, selectedFlowType) => {
  if (selectedOrderVerticalId === LEARN_VERTICAL_ID) {
    return LEARN_PROVIDERS;
  }

  if (selectedFlowType === FULL_PAYMENT) {
    return SCA_PROVIDERS.filter(
      (provider) => provider.name !== "IN_HOUSE_PART_PAYMENT"
    );
  }
  if (selectedOrderVerticalId === KH_VERTICAL_ID) {
    return SCA_PROVIDERS;
  }
};

const PartPayment = (props) => {
  const {
    selectedOrderId,
    isPartPaymentScheduleConfirmed,
    selectedPartPaymentDetails,
    isAnyAmountPaid,
    salesPersonId,
    selectedOrderVerticalId,
    selectedFlowType,
    fromSwitchToLoan = false,
    currencyId,
    countryId,
    currencyCode,
  } = props;
  const { openSnackbar } = useSnackbarContext();
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [paymentLinks, setPaymentLinks] = useState({});
  const [isLinkCopied, setIsLinkCopied] = useState({});
  const [customAmount, setCustomAmount] = useState("");
  const [customPaymentLink, setCustomPaymentLink] = useState(null);
  const [openDialogContent, setOpenDialogContent] = useState(null);
  const [isChangeSchedule, setIsChangeSchedule] = useState(false);
  const [provider, setProvider] = useState(() =>
    getDefaultProvider(selectedPartPaymentDetails) ?? null,
  );

  useEffect(() => {
    if (isPartPaymentScheduleConfirmed && selectedPartPaymentDetails) {
      const defaultProvider = getDefaultProvider(selectedPartPaymentDetails);
      if (defaultProvider != null) {
        setProvider(defaultProvider);
      }
    }
  }, [isPartPaymentScheduleConfirmed, selectedPartPaymentDetails]);

  const shouldFetchPartPaymentSchedules =
    !fromSwitchToLoan &&
    (!isPartPaymentScheduleConfirmed || isChangeSchedule) &&
    !!provider;
  const {
    data: partPaymentDetails,
    isFetching: isPartPaymentDetailsFetching,
    isError: isErrorOnFetchPartPaymentDetails,
    error: errorMessageOnFetchSchedules,
  } = useFetchPartPaymentDetails(selectedOrderId, provider, {
    enabled: shouldFetchPartPaymentSchedules,
  });

  const { mutate: createPaymentLink, pending: isCreatePaymentLinkPending } =
    useCreatePaymentLink();

  const {
    mutate: createPartPaymentSchedule,
    isPending: isPartPaymentSchedulePending,
  } = useCreatePartPaymentSchedule();

  const partOptions = partPaymentDetails?.installment_plans
    ? partPaymentDetails.installment_plans.map((plan) => ({
        label: `${plan.total_schedules} parts`,
        value: plan.total_schedules.toString(),
      }))
    : [];

  useEffect(() => {
    if (partOptions.length > 0 && !selectedOption) {
      setSelectedOption(partOptions[0].value);
    }
  }, [partOptions, selectedOption]);

  const selectedPartData = partPaymentDetails?.installment_plans?.find(
    (item) => item?.total_schedules?.toString?.() === selectedOption
  );

  const currentPartSchedule = selectedPartData?.schedules ?? [];

  const installmentDetails = getInstallments(
    isChangeSchedule,
    isPartPaymentScheduleConfirmed,
    currentPartSchedule,
    selectedPartPaymentDetails
  );

  const isPartAmountFullyPaid = (installmentDetail) =>
    installmentDetail?.status?.id === PART_PAYMENT_FULLY_PAID_STATUS_ID;

  const isPartAmountPartiallyPaid = (installmentDetail) =>
    installmentDetail?.status?.id === PART_PAYMENT_PARTIALLY_PAID_STATUS_ID;

  const showCreatePaymentLink = (
    installmentDetail,
    installmentIndex,
    installmentDetails,
    provider
  ) => {
    const firstNotFullyPaidIndex = installmentDetails.findIndex(
      (inst) => !isPartAmountFullyPaid(inst)
    );
    if (provider === JODO_FLEX.id) {
      return false;
    }
    return (
      isPartPaymentScheduleConfirmed &&
      !isChangeSchedule &&
      installmentIndex === firstNotFullyPaidIndex &&
      !isPartAmountFullyPaid(installmentDetail)
    );
  };

  useEffect(() => {
    if (installmentDetails.length > 0) {
      const currentIndex = installmentDetails.findIndex(
        (inst) => !isPartAmountFullyPaid(inst)
      );
      setActiveStep(
        currentIndex === -1 ? installmentDetails.length - 1 : currentIndex
      );
    }
  }, [installmentDetails]);

  if (isErrorOnFetchPartPaymentDetails) {
    return (
      <ErrorMessage>
        {errorMessageOnFetchSchedules?.message ||
          "Failed to fetch part payment details"}
      </ErrorMessage>
    );
  }

  const firstInstallment = installmentDetails.find(
    (installment) => installment.installment_no === FIRST_INSTALLMENT_NUMBER
  );
  const firstInstallmentRemainingAmount = firstInstallment
    ? firstInstallment.amount - firstInstallment.amount_paid
    : null;
  const orderMinAmount =
    firstInstallmentRemainingAmount > 0
      ? Number(firstInstallmentRemainingAmount)
      : 0;
  const requiredMinAmount = Math.max(ONE_RUPEE, orderMinAmount);
  const showBelowMinAmountError =
    customAmount !== "" && Number(customAmount) < requiredMinAmount;
  const showFirstInstallmentMinError =
    showBelowMinAmountError && orderMinAmount === requiredMinAmount;

  const providerOptions =
    getProvidersList(selectedOrderVerticalId, selectedFlowType) ??
    SCA_PROVIDERS;

  return (
    <div className={styles.mainContainer}>
      <div
        key="provider-select"
        className={`${styles.radioButtonContainer} ${styles.leftCard}`}
      >
        <div className={styles.field}>
          <Text>Select Provider</Text>
          <SingleSelect
            value={provider}
            menuItems={providerOptions}
            handleChange={(event) => setProvider(event.target.value)}
            tag="Select Provider"
            disabled={isPartPaymentScheduleConfirmed}
          />
        </div>
      </div>
      {!provider && !isPartPaymentScheduleConfirmed ? (
        <ErrorMessage>
          Please select a provider to see part payment details
        </ErrorMessage>
      ) : isPartPaymentDetailsFetching ? (
        <Loader />
      ) : !selectedPartPaymentDetails && partOptions.length === 0 ? (
        <>
          <ErrorMessage>No Part Payment Details Available</ErrorMessage>
        </>
      ) : (
        <>
          <div
            className={
              isPartPaymentScheduleConfirmed && !isChangeSchedule
                ? `${styles.radioButtonContainer} ${styles.radioButtonContainerConfirmed}`
                : styles.radioButtonContainer
            }
          >
            {!isPartPaymentScheduleConfirmed || isChangeSchedule ? (
              <>
                <Text>Select part</Text>
                <RadioButton
                  value={selectedOption}
                  options={partOptions}
                  onChange={(event) =>
                    handlePartChange(
                      event,
                      setSelectedOption,
                      setActiveStep,
                      setPaymentLinks,
                      setIsLinkCopied
                    )
                  }
                  row
                />
              </>
            ) : (
              <div className={styles.scheduleContainer}>
                <Text>
                  Selected Schedule:{" "}
                  {selectedPartPaymentDetails?.[0]?.installments?.length ||
                    "N/A"}{" "}
                  parts
                </Text>
                {!isAnyAmountPaid && (
                  <div
                    className={styles.changeSchedule}
                    onClick={() => setIsChangeSchedule(true)}
                  >
                    Change schedule
                    <EditIcon />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.partPaymentContainer}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {installmentDetails.map((installmentDetail, installmentIndex) => (
                <Step
                  className={styles.partPaymentDetails}
                  key={installmentDetail.installment_no}
                  completed={isPartAmountFullyPaid(installmentDetail)}
                >
                  <StepLabel>
                    {formatMonetaryAmount(
                      installmentDetail.amount,
                      currencyCode,
                    )}{" "}
                    - Due on {installmentDetail.due_date}
                    {isPartPaymentScheduleConfirmed &&
                      isPartAmountFullyPaid(installmentDetail) && (
                        <span className={styles.fullyPaidLabel}>
                          {" "}
                          Amount Paid
                        </span>
                      )}
                  </StepLabel>
                  {showCreatePaymentLink(
                    installmentDetail,
                    installmentIndex,
                    installmentDetails,
                    provider
                  ) && (
                    <PrimaryButton
                      onClick={() =>
                        handleOnClickCreatePaymentLink(
                          selectedOrderId,
                          installmentIndex,
                          installmentDetail.amount,
                          installmentDetail.amount_paid,
                          createPaymentLink,
                          setPaymentLinks,
                          setIsLinkCopied,
                          isPartAmountPartiallyPaid(installmentDetail),
                          currencyId,
                        )
                      }
                    >
                      {isCreatePaymentLinkPending ? (
                        <Loader />
                      ) : (
                        <>
                          Create Payment Link for{" "}
                          {formatMonetaryAmount(
                            isPartAmountPartiallyPaid(installmentDetail)
                              ? installmentDetail.amount -
                                  installmentDetail.amount_paid
                              : installmentDetail.amount,
                            currencyCode,
                          )}
                        </>
                      )}
                    </PrimaryButton>
                  )}
                  {paymentLinks[installmentIndex] && (
                      <div className={styles.paymentLinkContainer}>
                        <div
                          className={`${styles.copyLinkContainer} ${isLinkCopied[installmentIndex] ? styles.copyLinkCopied : ""}`}
                          onClick={() =>
                            !isLinkCopied[installmentIndex] &&
                            handleCopyLink(
                              installmentIndex,
                              paymentLinks,
                              setIsLinkCopied
                            )
                          }
                        >
                          <div className={isLinkCopied[installmentIndex] ? styles.copyLinkTextCopied : styles.copyLinkText}>
                            {isLinkCopied[installmentIndex] ? "Copied!" : "Copy Link"}
                          </div>
                          <ContentCopyIcon className={styles.copyIcon} />
                        </div>
                      </div>
                    )}
                </Step>
              ))}
            </Stepper>
          </div>
          {(!isPartPaymentScheduleConfirmed || isChangeSchedule) && (
            <PrimaryButton onClick={() => setOpenDialogContent(true)}>
              Confirm Schedule
            </PrimaryButton>
          )}
          {showCopyMandateLinkButton(
            selectedPartPaymentDetails,
            handleCopyMandateLink
          )}
          <div className={styles.dashedLine} />
          {isPartPaymentScheduleConfirmed && !isChangeSchedule && (
            <div className={styles.customAmount}>
              <div className={styles.field}>
                <Text>Collect Payment For Custom Amount</Text>
                <Textfield
                  label="Enter Amount"
                  value={customAmount}
                  handleOnChange={(event) => {
                    const value = event.target.value;
                    const validAmount = value
                      .replace(/[^0-9.]/g, "")
                      .replace(/(\..*?)\..*/g, "$1");

                    setCustomAmount(validAmount);
                  }}
                  placeholder="Enter Amount"
                />
                {showFirstInstallmentMinError && (
                  <ErrorMessage>
                    {`Please pay ${formatMonetaryAmount(
                      firstInstallmentRemainingAmount,
                      currencyCode,
                    )} or more for the first installment.`}
                  </ErrorMessage>
                )}
                {showBelowMinAmountError && !showFirstInstallmentMinError && (
                  <ErrorMessage>
                    {`Payment amount must be at least ${formatMonetaryAmount(
                      ONE_RUPEE,
                      currencyCode,
                    )}.`}
                  </ErrorMessage>
                )}
              </div>
              <PaymentModes
                orderId={selectedOrderId}
                amountToBePaid={customAmount}
                createdBy={salesPersonId}
                paymentLink={customPaymentLink}
                setPaymentLink={setCustomPaymentLink}
                paymentTypeId={CUSTOM_AMOUNT_PAYMENT_TYPE_ID}
                paymentFlowTypeId={PAYMENT_PLANS.PART_PAYMENT}
                firstInstallmentRemainingAmount={
                  firstInstallmentRemainingAmount
                }
                selectedOrderVerticalId={selectedOrderVerticalId}
                currencyId={currencyId}
                countryId={countryId}
              />
            </div>
          )}
        </>
      )}

      <Dialog
        open={openDialogContent}
        handleClose={() => handleCloseConfirmDialog(setOpenDialogContent)}
        width="500px"
      >
        <ConfirmSchedule
          onCancel={() => handleCloseConfirmDialog(setOpenDialogContent)}
          onConfirm={() =>
            handleConfirmAndProceed(
              partPaymentDetails,
              selectedOption,
              selectedOrderId,
              createPartPaymentSchedule,
              provider,
              openSnackbar
            )
          }
          isPending={isPartPaymentSchedulePending}
          isError={false}
          parts={selectedOption}
        />
      </Dialog>
    </div>
  );
};

export default PartPayment;
